import type { StudioProjectOutputKind } from "@/features/project/lib/studio-project-plan";
import type { RailItemId } from "./types";
import {
  Compass,
  Component,
  Database,
  Download,
  Eye,
  FileText,
  Frame,
  History,
  Layers,
  ListChecks,
  Palette,
  Paintbrush,
  Radar,
  Rocket,
  Settings,
  ShieldCheck,
  Upload,
  Zap,
  type LucideIcon,
} from "lucide-react";

// "as-is" (G1 alternative A — approved 2026-08-12): the 5th project-internal
// mode, reading the captured original screen. It carries no rail-item
// sub-navigation (DOMAIN_RENEWAL_RAIL below is empty for it) and exactly one
// authority task (DOMAIN_TASKS below) whose sole job is to park
// `activeRailItem` on "source" so `IDEShellLayout`'s `ActiveSurface` can
// disambiguate it from the "design" domain, which also lives on "source".
export type StudioAuthorityDomain =
  | "primary"
  | "as-is"
  | "design"
  | "guide"
  | "insights";

export interface StudioAuthorityTask {
  id: string;
  label: string;
  description: string;
  railItem: RailItemId;
  surfaceId: string;
}

export type StudioRenewalRailKey =
  | "app.preview"
  | "app.pages"
  | "app.action"
  | "app.data"
  | "app.check"
  | "app.publish"
  | "app.launch"
  | "app.settings"
  | "document.preview"
  | "document.sections"
  | "document.body"
  | "document.sources"
  | "document.export"
  | "document.settings"
  | "game.preview"
  | "game.scenes"
  | "game.rules"
  | "game.data"
  | "game.check"
  | "game.publish"
  | "game.launch"
  | "game.settings"
  | "design.canvas"
  | "design.tokens"
  | "design.components"
  | "design.settings"
  | "guide.body"
  | "guide.sections"
  | "guide.style"
  | "guide.settings"
  | "insights.overview"
  | "insights.coverage"
  | "insights.records"
  | "insights.quality"
  | "insights.settings";

/**
 * The renewal rail is a presentation adapter over canonical authority tasks.
 * Existing task ids remain stable for Chat/WebMCP, deep links, and receipts;
 * entries without a task id route directly to an already-shipped surface.
 */
export interface StudioRenewalRailItem {
  id: StudioRenewalRailKey;
  taskId?: string;
  railItem: RailItemId;
  icon: LucideIcon;
  dividerBefore?: boolean;
}

const PRIMARY_RENEWAL_RAIL: Record<
  StudioProjectOutputKind,
  readonly StudioRenewalRailItem[]
> = {
  app: [
    { id: "app.preview", taskId: "overview", railItem: "preview", icon: Eye },
    { id: "app.pages", taskId: "structure", railItem: "editor", icon: Layers },
    { id: "app.action", taskId: "behavior", railItem: "behavior", icon: Zap },
    { id: "app.data", taskId: "data", railItem: "data", icon: Database },
    {
      id: "app.check",
      taskId: "test",
      railItem: "evidence",
      icon: ShieldCheck,
      dividerBefore: true,
    },
    { id: "app.publish", taskId: "publish", railItem: "publish", icon: Upload },
    { id: "app.launch", taskId: "launch", railItem: "deploy", icon: Rocket },
    {
      id: "app.settings",
      taskId: "settings",
      railItem: "settings",
      icon: Settings,
    },
  ],
  document: [
    {
      id: "document.preview",
      taskId: "overview",
      railItem: "preview",
      icon: Eye,
    },
    {
      id: "document.sections",
      taskId: "structure",
      railItem: "document",
      icon: Layers,
    },
    {
      id: "document.body",
      taskId: "content",
      railItem: "document",
      icon: FileText,
    },
    {
      id: "document.sources",
      taskId: "sources",
      railItem: "document",
      icon: ShieldCheck,
    },
    {
      id: "document.export",
      taskId: "export",
      railItem: "document",
      icon: Download,
      dividerBefore: true,
    },
    {
      id: "document.settings",
      taskId: "settings",
      railItem: "settings",
      icon: Settings,
    },
  ],
  game: [
    { id: "game.preview", taskId: "overview", railItem: "preview", icon: Eye },
    { id: "game.scenes", taskId: "scenes", railItem: "editor", icon: Layers },
    { id: "game.rules", taskId: "rules", railItem: "behavior", icon: Zap },
    { id: "game.data", taskId: "data", railItem: "data", icon: Database },
    {
      id: "game.check",
      taskId: "playtest",
      railItem: "evidence",
      icon: ShieldCheck,
      dividerBefore: true,
    },
    {
      id: "game.publish",
      taskId: "publish",
      railItem: "publish",
      icon: Upload,
    },
    { id: "game.launch", taskId: "launch", railItem: "deploy", icon: Rocket },
    {
      id: "game.settings",
      taskId: "settings",
      railItem: "settings",
      icon: Settings,
    },
  ],
};

const DOMAIN_RENEWAL_RAIL: Record<
  Exclude<StudioAuthorityDomain, "primary">,
  readonly StudioRenewalRailItem[]
> = {
  // As-Is is a single read-only view of the captured original — no
  // sub-navigation, so the rail shows nothing for it beyond the
  // persona-gated "advanced" items GroupedRail already adds independently.
  "as-is": [],
  design: [
    { id: "design.canvas", taskId: "sources", railItem: "source", icon: Frame },
    {
      id: "design.tokens",
      taskId: "system",
      railItem: "source",
      icon: Palette,
    },
    {
      id: "design.components",
      taskId: "components",
      railItem: "source",
      icon: Component,
    },
    {
      id: "design.settings",
      taskId: "settings",
      railItem: "settings",
      icon: Settings,
    },
  ],
  guide: [
    {
      id: "guide.body",
      taskId: "content",
      railItem: "document",
      icon: FileText,
    },
    {
      id: "guide.sections",
      taskId: "overview",
      railItem: "document",
      icon: Layers,
    },
    {
      id: "guide.style",
      taskId: "audiences",
      railItem: "document",
      icon: Paintbrush,
    },
    {
      id: "guide.settings",
      taskId: "settings",
      railItem: "settings",
      icon: Settings,
    },
  ],
  insights: [
    {
      id: "insights.overview",
      taskId: "overview",
      railItem: "report",
      icon: Compass,
    },
    {
      id: "insights.coverage",
      taskId: "sufficiency",
      railItem: "evidence",
      icon: Radar,
    },
    {
      id: "insights.records",
      taskId: "release",
      railItem: "report",
      icon: History,
    },
    {
      id: "insights.quality",
      taskId: "quality",
      railItem: "evidence",
      icon: ListChecks,
      dividerBefore: true,
    },
    {
      id: "insights.settings",
      taskId: "settings",
      railItem: "settings",
      icon: Settings,
    },
  ],
};

const PRIMARY_TASKS: Record<
  StudioProjectOutputKind,
  readonly StudioAuthorityTask[]
> = {
  app: [
    {
      id: "overview",
      label: "Overview",
      description: "Preview and operate the application",
      railItem: "preview",
      surfaceId: "preview",
    },
    {
      id: "structure",
      label: "Structure",
      description: "Organize pages, blocks, and elements",
      railItem: "editor",
      surfaceId: "page-editor",
    },
    {
      id: "behavior",
      label: "Behavior",
      description: "Define events and ActionFlows",
      railItem: "behavior",
      surfaceId: "behavior",
    },
    {
      id: "data",
      label: "Data",
      description: "Connect data sources and bindings",
      railItem: "data",
      surfaceId: "data",
    },
    {
      id: "test",
      label: "Test",
      description: "Verify the application result",
      railItem: "evidence",
      surfaceId: "evidence",
    },
    {
      id: "publish",
      label: "Publish",
      description: "Prepare an approved application release",
      railItem: "publish",
      surfaceId: "publish",
    },
    {
      id: "launch",
      label: "Launch",
      description: "Launch and operate the application",
      railItem: "deploy",
      surfaceId: "deploy",
    },
    {
      id: "settings",
      label: "Settings",
      description: "Configure the application project",
      railItem: "settings",
      surfaceId: "settings",
    },
  ],
  document: [
    {
      id: "overview",
      label: "Overview",
      description: "Preview the document artifact",
      railItem: "preview",
      surfaceId: "preview",
    },
    {
      id: "structure",
      label: "Structure",
      description: "Organize document pages and sections",
      railItem: "document",
      surfaceId: "document-workspace",
    },
    {
      id: "content",
      label: "Content",
      description: "Write and revise document content",
      railItem: "document",
      surfaceId: "document-workspace",
    },
    {
      id: "sources",
      label: "Sources",
      description: "Manage document evidence and citations",
      railItem: "document",
      surfaceId: "document-workspace",
    },
    {
      id: "export",
      label: "Export",
      description: "Prepare native document exports",
      railItem: "document",
      surfaceId: "document-workspace",
    },
    {
      id: "settings",
      label: "Settings",
      description: "Configure the document project",
      railItem: "settings",
      surfaceId: "settings",
    },
  ],
  game: [
    {
      id: "overview",
      label: "Overview",
      description: "Preview and play the game",
      railItem: "preview",
      surfaceId: "preview",
    },
    {
      id: "scenes",
      label: "Scenes",
      description: "Organize scenes and game screens",
      railItem: "editor",
      surfaceId: "page-editor",
    },
    {
      id: "rules",
      label: "Rules",
      description: "Define game rules and ActionFlows",
      railItem: "behavior",
      surfaceId: "behavior",
    },
    {
      id: "data",
      label: "Data",
      description: "Manage game state and data",
      railItem: "data",
      surfaceId: "data",
    },
    {
      id: "playtest",
      label: "Playtest",
      description: "Verify playable behavior",
      railItem: "evidence",
      surfaceId: "evidence",
    },
    {
      id: "publish",
      label: "Publish",
      description: "Prepare an approved game release",
      railItem: "publish",
      surfaceId: "publish",
    },
    {
      id: "launch",
      label: "Launch",
      description: "Launch and operate the game",
      railItem: "deploy",
      surfaceId: "deploy",
    },
    {
      id: "settings",
      label: "Settings",
      description: "Configure the game project",
      railItem: "settings",
      surfaceId: "settings",
    },
  ],
};

const DOMAIN_TASKS: Record<
  Exclude<StudioAuthorityDomain, "primary">,
  readonly StudioAuthorityTask[]
> = {
  "as-is": [
    {
      id: "original",
      // Untranslated fallback only — the localized copy for all five locales
      // lives at `as-is.original` in i18n/studio-authority-copy.ts. Wording
      // matches it: what was captured is a structure READ OUT of the original
      // (field names, column headers, action labels), not the screen itself.
      label: "Original structure",
      description: "Review the structure read from the original — not a reproduction of it",
      // Deliberately "source" — the rail item "design" already owns via
      // DOMAIN_RENEWAL_RAIL.design. IDEShellLayout's ActiveSurface
      // disambiguates the two by authorityDomain within `case 'source'`
      // (see IDEShellLayout.tsx). This is NOT "preview": ActiveCanvasBody
      // would otherwise intercept it and mount BuildModeCanvas instead of
      // ever reaching ActiveSurface.
      railItem: "source",
      surfaceId: "as-is",
    },
  ],
  design: [
    {
      id: "system",
      label: "System",
      description: "Manage tokens and design-system rules",
      railItem: "source",
      surfaceId: "source",
    },
    {
      id: "sources",
      label: "Sources",
      description: "Review Figma and visual source evidence",
      railItem: "source",
      surfaceId: "source",
    },
    {
      id: "components",
      label: "Components",
      description: "Review reusable components and variants",
      railItem: "source",
      surfaceId: "source",
    },
    {
      id: "states",
      label: "States",
      description: "Review responsive and accessible states",
      railItem: "source",
      surfaceId: "source",
    },
    {
      id: "compare",
      label: "Compare",
      description: "Compare source and generated structure",
      railItem: "map",
      surfaceId: "map",
    },
    {
      id: "settings",
      label: "Settings",
      description: "Configure the design workspace",
      railItem: "settings",
      surfaceId: "settings",
    },
  ],
  guide: [
    {
      id: "overview",
      label: "Overview",
      description: "Review companion guidance",
      railItem: "document",
      surfaceId: "document-workspace",
    },
    {
      id: "content",
      label: "Content",
      description: "Write onboarding and operating guidance",
      railItem: "document",
      surfaceId: "document-workspace",
    },
    {
      id: "audiences",
      label: "Audiences",
      description: "Adapt guidance for user roles",
      railItem: "document",
      surfaceId: "document-workspace",
    },
    {
      id: "localization",
      label: "Localization",
      description: "Manage localized guidance",
      railItem: "document",
      surfaceId: "document-workspace",
    },
    {
      id: "publish",
      label: "Publish",
      description: "Prepare approved guide outputs",
      railItem: "document",
      surfaceId: "document-workspace",
    },
    {
      id: "settings",
      label: "Settings",
      description: "Configure the guide workspace",
      railItem: "settings",
      surfaceId: "settings",
    },
  ],
  insights: [
    {
      id: "overview",
      label: "Overview",
      description: "Review readiness and ontology coverage",
      railItem: "report",
      surfaceId: "report-workspace",
    },
    {
      id: "sufficiency",
      label: "Sufficiency",
      description: "Assess whether source evidence is sufficient",
      railItem: "evidence",
      surfaceId: "evidence",
    },
    {
      id: "quality",
      label: "Quality",
      description: "Review UI, behavior, and artifact quality",
      railItem: "evidence",
      surfaceId: "evidence",
    },
    {
      id: "runtime",
      label: "Runtime",
      description: "Inspect runtime traces and failures",
      railItem: "report",
      surfaceId: "report-workspace",
    },
    {
      id: "release",
      label: "Release",
      description: "Review release blockers and proof",
      railItem: "report",
      surfaceId: "report-workspace",
    },
    {
      id: "live",
      label: "Live",
      description: "Review production feedback and checks",
      railItem: "report",
      surfaceId: "report-workspace",
    },
    {
      id: "settings",
      label: "Settings",
      description: "Configure the insights workspace",
      railItem: "settings",
      surfaceId: "settings",
    },
  ],
};

export const STUDIO_AUTHORITY_DOMAINS: readonly StudioAuthorityDomain[] = [
  "primary",
  "as-is",
  "design",
  "guide",
  "insights",
];

export function getStudioRenewalRailItems(
  domain: StudioAuthorityDomain,
  outputKind: StudioProjectOutputKind,
): readonly StudioRenewalRailItem[] {
  return domain === "primary"
    ? PRIMARY_RENEWAL_RAIL[outputKind]
    : DOMAIN_RENEWAL_RAIL[domain];
}

export function getDefaultStudioRenewalAuthorityView(
  domain: StudioAuthorityDomain,
  outputKind: StudioProjectOutputKind,
): string | undefined {
  return getStudioRenewalRailItems(domain, outputKind).find(
    (item) => item.taskId,
  )?.taskId;
}

export function getStudioAuthorityTasks(
  domain: StudioAuthorityDomain,
  outputKind: StudioProjectOutputKind,
): readonly StudioAuthorityTask[] {
  return domain === "primary"
    ? PRIMARY_TASKS[outputKind]
    : DOMAIN_TASKS[domain];
}

export function getDefaultStudioAuthorityTask(
  domain: StudioAuthorityDomain,
  outputKind: StudioProjectOutputKind,
): StudioAuthorityTask {
  return getStudioAuthorityTasks(domain, outputKind)[0];
}

export function findStudioAuthorityTask(
  domain: StudioAuthorityDomain,
  view: string | undefined,
  outputKind: StudioProjectOutputKind,
): StudioAuthorityTask {
  const tasks = getStudioAuthorityTasks(domain, outputKind);
  return tasks.find((task) => task.id === view) ?? tasks[0];
}

export function findStudioAuthorityTaskByRailItem(
  domain: StudioAuthorityDomain,
  railItem: RailItemId,
  outputKind: StudioProjectOutputKind,
): StudioAuthorityTask {
  const tasks = getStudioAuthorityTasks(domain, outputKind);
  return tasks.find((task) => task.railItem === railItem) ?? tasks[0];
}

/**
 * Which authority domain owns a rail item.
 *
 * The "primary" list below is spelled the same as
 * `SELECTION_BEARING_RAIL_ITEMS` in ./selection-context, and that sameness is
 * a coincidence, not a shared fact — do not deduplicate them. This function
 * answers "which domain owns this rail"; that set answers "can this rail show
 * a selection". They agree today only because the primary domain happens to
 * be the app-building domain. A rail added to the primary domain that renders
 * no element tree (the way Review, Publish, Launch, and Settings already do,
 * from PRIMARY_RENEWAL_RAIL above) would belong here and NOT there, and
 * pointing one at the other would silently re-admit the stale selection
 * #2551 removed.
 */
export function resolveStudioAuthorityDomain(
  railItem: RailItemId,
): StudioAuthorityDomain | null {
  if (["preview", "editor", "behavior", "data", "library"].includes(railItem))
    return "primary";
  if (["source", "map"].includes(railItem)) return "design";
  if (railItem === "document") return "guide";
  if (["report", "evidence"].includes(railItem)) return "insights";
  return null;
}

export function studioAuthorityNamespace(
  domain: StudioAuthorityDomain,
): string {
  return domain === "primary" ? "studio.primary" : `studio.${domain}`;
}

export function updateStudioAuthorityDeepLink(
  domain: StudioAuthorityDomain,
  view: string,
): void {
  const url = new URL(window.location.href);
  url.searchParams.set("studioDomain", domain);
  url.searchParams.set("studioView", view);
  window.history.replaceState(window.history.state, "", url);
}

export function readStudioAuthorityDeepLink(
  outputKind: StudioProjectOutputKind,
): { domain: StudioAuthorityDomain; task: StudioAuthorityTask } | null {
  const params = new URLSearchParams(window.location.search);
  const domain = params.get("studioDomain");
  if (!STUDIO_AUTHORITY_DOMAINS.includes(domain as StudioAuthorityDomain))
    return null;
  const resolvedDomain = domain as StudioAuthorityDomain;
  return {
    domain: resolvedDomain,
    task: findStudioAuthorityTask(
      resolvedDomain,
      params.get("studioView") ?? undefined,
      outputKind,
    ),
  };
}
